import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Home, Eye, EyeOff, RefreshCw, LogOut, Edit2, Save, X, Upload, Download, Volume2, Sparkles, Hash, Trophy, FileDown, FileUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { AdminContext } from "./admin/adminShared";
import { CountriesTab } from "./admin/CountriesTab";
import { VotesTab } from "./admin/VotesTab";
import { ControlTab } from "./admin/ControlTab";
import { ConfigTab } from "./admin/ConfigTab";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveUrl = (url) => {
  if (!url) return "";
  if (url.startsWith('data:')) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    const uploadsMatch = url.match(/\/api\/uploads\/.+$/);
    if (uploadsMatch) return `${BACKEND_URL}${uploadsMatch[0]}`;
    const legacyMatch = url.match(/(\/uploads\/.+)$/);
    if (legacyMatch) return `${BACKEND_URL}/api${legacyMatch[1]}`;
    return url;
  }
  if (url.startsWith('/uploads/') && !url.startsWith('/api/uploads/'))
    return `${BACKEND_URL}/api${url}`;
  if (url.startsWith('/api/'))
    return `${BACKEND_URL}${url}`;
  return `${BACKEND_URL}${url}`;
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [votes, setVotes] = useState([]);
  // Multi-event support
  const [events, setEvents] = useState([]);
  const [activeEvent, setActiveEvent] = useState(null);
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [newEventName, setNewEventName] = useState("");
  const [copyFromEventId, setCopyFromEventId] = useState("none");
  const [creatingEvent, setCreatingEvent] = useState(false);
  const [showRenameEvent, setShowRenameEvent] = useState(false);
  const [renameEventName, setRenameEventName] = useState("");
  const [showDeleteEvent, setShowDeleteEvent] = useState(false);
  const [deleteEventConfirmText, setDeleteEventConfirmText] = useState("");
  const [deletingEvent, setDeletingEvent] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [importing, setImporting] = useState(false);
  const [newCountry, setNewCountry] = useState({
    name: "",
    password: "",
    flag_url: "",
    artist_photo_url: "",
    artist_name: "",
    song_title: "",
    overlay_photo_url: "",
    is_jury: false,
    running_order: 0
  });
  const [editingCountry, setEditingCountry] = useState(null);
  const [backgroundVideo, setBackgroundVideo] = useState("");
  const [bgVideoBlur, setBgVideoBlur] = useState(10);
  const [bgVideoBrightness, setBgVideoBrightness] = useState(100);
  const [bgVideoOpacity, setBgVideoOpacity] = useState(20);
  const [fontTitle, setFontTitle] = useState("Unbounded");
  const [fontSubtitle, setFontSubtitle] = useState("Inter");
  const [fontCountry, setFontCountry] = useState("Inter");
  const [fontPoints, setFontPoints] = useState("Chivo Mono");
  const [fontVoting, setFontVoting] = useState("Inter");
  const [fontTitleItalic, setFontTitleItalic] = useState(false);
  const [fontSubtitleItalic, setFontSubtitleItalic] = useState(false);
  const [fontCountryItalic, setFontCountryItalic] = useState(false);
  const [fontPointsItalic, setFontPointsItalic] = useState(false);
  const [fontVotingItalic, setFontVotingItalic] = useState(false);
  const [fontVotingSecondaryItalic, setFontVotingSecondaryItalic] = useState(false);
  // Custom fonts for each section
  const [customFontTitleUrl, setCustomFontTitleUrl] = useState("");
  const [customFontTitleName, setCustomFontTitleName] = useState("");
  const [customFontSubtitleUrl, setCustomFontSubtitleUrl] = useState("");
  const [customFontSubtitleName, setCustomFontSubtitleName] = useState("");
  const [customFontCountryUrl, setCustomFontCountryUrl] = useState("");
  const [customFontCountryName, setCustomFontCountryName] = useState("");
  const [customFontPointsUrl, setCustomFontPointsUrl] = useState("");
  const [customFontPointsName, setCustomFontPointsName] = useState("");
  const [customFontVotingUrl, setCustomFontVotingUrl] = useState("");
  const [customFontVotingName, setCustomFontVotingName] = useState("");
  // Font sizes
  const [fontSizeTitle, setFontSizeTitle] = useState(48);
  const [fontSizeSubtitle, setFontSizeSubtitle] = useState(16);
  const [fontSizeCountry, setFontSizeCountry] = useState(14);
  const [fontSizePoints, setFontSizePoints] = useState(24);
  const [fontSizeVoting, setFontSizeVoting] = useState(20);
  // Theme colors
  const [primaryColor, setPrimaryColor] = useState("#EC4899");
  const [secondaryColor, setSecondaryColor] = useState("#22D3EE");
  const [tertiaryColor, setTertiaryColor] = useState("#8B5CF6");
  // Row colors
  const [rowBgEven, setRowBgEven] = useState("rgba(24, 24, 27, 0.9)");
  const [rowBgOdd, setRowBgOdd] = useState("rgba(39, 39, 42, 0.7)");
  const [rowBgEvenTranslucent, setRowBgEvenTranslucent] = useState(false);
  const [rowBgOddTranslucent, setRowBgOddTranslucent] = useState(false);
  const [rowBadgeTranslucent, setRowBadgeTranslucent] = useState(false);
  const [logoUrl, setLogoUrl] = useState("");
  const [logoPosition, setLogoPosition] = useState("top-left");
  const [logoSize, setLogoSize] = useState(80);
  const [logoZIndex, setLogoZIndex] = useState(50);
  const [logoOpacity, setLogoOpacity] = useState(100);
  const [logoX, setLogoX] = useState(null);
  const [logoY, setLogoY] = useState(null);
  const [extraImage1Url, setExtraImage1Url] = useState("");
  const [extraImage1Position, setExtraImage1Position] = useState("top-right");
  const [extraImage1Size, setExtraImage1Size] = useState(80);
  const [extraImage1ZIndex, setExtraImage1ZIndex] = useState(50);
  const [extraImage1Opacity, setExtraImage1Opacity] = useState(100);
  const [extraImage1X, setExtraImage1X] = useState(null);
  const [extraImage1Y, setExtraImage1Y] = useState(null);
  const [extraImage2Url, setExtraImage2Url] = useState("");
  const [extraImage2Position, setExtraImage2Position] = useState("bottom-left");
  const [extraImage2Size, setExtraImage2Size] = useState(80);
  const [extraImage2ZIndex, setExtraImage2ZIndex] = useState(50);
  const [extraImage2Opacity, setExtraImage2Opacity] = useState(100);
  const [extraImage2X, setExtraImage2X] = useState(null);
  const [extraImage2Y, setExtraImage2Y] = useState(null);
  const [extraImage3Url, setExtraImage3Url] = useState("");
  const [extraImage3Position, setExtraImage3Position] = useState("bottom-right");
  const [extraImage3Size, setExtraImage3Size] = useState(80);
  const [extraImage3ZIndex, setExtraImage3ZIndex] = useState(50);
  const [extraImage3Opacity, setExtraImage3Opacity] = useState(100);
  const [extraImage3X, setExtraImage3X] = useState(null);
  const [extraImage3Y, setExtraImage3Y] = useState(null);
  const [defaultArtistPhoto, setDefaultArtistPhoto] = useState("");
  const [artistPhotoSizeMode, setArtistPhotoSizeMode] = useState("adaptive");
  const [artistPhotoSizePx, setArtistPhotoSizePx] = useState(300);
  // Customizable texts
  const [textTitle, setTextTitle] = useState("CLASIFICACIÓN EN VIVO");
  const [textSubtitle, setTextSubtitle] = useState("Sistema de Votación Eurovisión");
  const [textVotingNow, setTextVotingNow] = useState("VOTANDO AHORA");
  const [textWaiting, setTextWaiting] = useState("Esperando próxima votación...");
  // Sound effect for 12 points
  const [sound12PointsUrl, setSound12PointsUrl] = useState("");
  const [soundWinnerUrl, setSoundWinnerUrl] = useState("");
  // Confetti effect
  const [confettiEnabled, setConfettiEnabled] = useState(true);
  const [confettiColors, setConfettiColors] = useState(["#EC4899", "#22D3EE", "#8B5CF6", "#FFD700", "#FF6B6B"]);
  // Top 3 custom colors (during voting)
  const [top1Color, setTop1Color] = useState("#EC4899");
  const [top2Color, setTop2Color] = useState("#8B5CF6");
  const [top3Color, setTop3Color] = useState("#22D3EE");
  const [lastPlaceColor, setLastPlaceColor] = useState("#8B2020");
  const [top1Translucent, setTop1Translucent] = useState(true);
  const [top2Translucent, setTop2Translucent] = useState(true);
  const [top3Translucent, setTop3Translucent] = useState(true);
  const [lastPlaceTranslucent, setLastPlaceTranslucent] = useState(false);
  const [positionBadgeTranslucent, setPositionBadgeTranslucent] = useState(false);
  // Scoreboard layout
  const [votingColumnCenter, setVotingColumnCenter] = useState(false);
  const [leftColSpan, setLeftColSpan] = useState(4);
  const [rightColSpan, setRightColSpan] = useState(4);
  const [votingColSpan, setVotingColSpan] = useState(4);
  const [columnGap, setColumnGap] = useState(6);
  const [votingWidthCustom, setVotingWidthCustom] = useState(false);
  const [votingWidthSpan, setVotingWidthSpan] = useState(4);
  const [voteBarColorMode, setVoteBarColorMode] = useState("original");
  const [voteBarColor1, setVoteBarColor1] = useState("#18181b");
  const [voteBarColor2, setVoteBarColor2] = useState("#27272a");
  const [voteBarTranslucent, setVoteBarTranslucent] = useState(false);
  const [voteBarOpacity, setVoteBarOpacity] = useState(100);
  // Subtitle counter
  const [subtitleCounterEnabled, setSubtitleCounterEnabled] = useState(false);
  const [subtitlePhaseOrder, setSubtitlePhaseOrder] = useState("jury_first");
  const [subtitleJuryLabel, setSubtitleJuryLabel] = useState("Jurado Profesional");
  const [subtitleTelevoteLabel, setSubtitleTelevoteLabel] = useState("Televoto");
  const [mystery12Text, setMystery12Text] = useState("?????????");
  const [mysteryHiddenPoints, setMysteryHiddenPoints] = useState("12");
  const [overlay12CustomFontUrl, setOverlay12CustomFontUrl] = useState("");
  const [winnerCustomFontUrl, setWinnerCustomFontUrl] = useState("");
  const [top6TitleText, setTop6TitleText] = useState("Current top 6:");
  const [top6StyleCustom, setTop6StyleCustom] = useState(false);
  const [top6FontTitle, setTop6FontTitle] = useState("Unbounded");
  const [top6FontCountry, setTop6FontCountry] = useState("Inter");
  const [voteDisplayColumns, setVoteDisplayColumns] = useState("single");
  const [showVoterBorder, setShowVoterBorder] = useState(true);
  const [showJuryBadge, setShowJuryBadge] = useState(true);
  const [fontVotingSecondary, setFontVotingSecondary] = useState("Inter");
  const [customFontVotingSecondaryUrl, setCustomFontVotingSecondaryUrl] = useState("");
  const [customFontVotingSecondaryName, setCustomFontVotingSecondaryName] = useState("");
  // 12-points overlay
  const [overlay12Enabled, setOverlay12Enabled] = useState(true);
  const [overlay12DurationMode, setOverlay12DurationMode] = useState("auto");
  const [overlay12DurationSeconds, setOverlay12DurationSeconds] = useState(8);
  const [overlay12BgStyle, setOverlay12BgStyle] = useState("gradient");
  const [overlay12BgColor, setOverlay12BgColor] = useState("#000000");
  const [overlay12BgOpacity, setOverlay12BgOpacity] = useState(80);
  const [overlay12TextReceives, setOverlay12TextReceives] = useState("OTORGA 12 PUNTOS A");
  const [overlay12UseAdminColors, setOverlay12UseAdminColors] = useState(true);
  const [overlay12ArrowColor, setOverlay12ArrowColor] = useState("");
  const [overlay12ArrowThickness, setOverlay12ArrowThickness] = useState(12);
  const [overlay12ArrowHeadSize, setOverlay12ArrowHeadSize] = useState(46);
  const [overlay12ShowConfetti, setOverlay12ShowConfetti] = useState(true);
  const [overlay12Visible, setOverlay12Visible] = useState(false);
  const [previewGiverId, setPreviewGiverId] = useState("");
  const [previewRecipientId, setPreviewRecipientId] = useState("");
  // Winner overlay
  const [winnerOverlayEnabled, setWinnerOverlayEnabled] = useState(true);
  const [winnerOverlayDurationMode, setWinnerOverlayDurationMode] = useState("manual");
  const [winnerOverlayDurationSeconds, setWinnerOverlayDurationSeconds] = useState(15);
  const [winnerOverlayBgStyle, setWinnerOverlayBgStyle] = useState("gradient");
  const [winnerOverlayBgColor, setWinnerOverlayBgColor] = useState("#000000");
  const [winnerOverlayBgOpacity, setWinnerOverlayBgOpacity] = useState(90);
  const [winnerOverlayTitleText, setWinnerOverlayTitleText] = useState("¡ENHORABUENA!");
  const [winnerOverlaySubtitleText, setWinnerOverlaySubtitleText] = useState("GANADOR");
  const [winnerOverlayUseAdminColors, setWinnerOverlayUseAdminColors] = useState(true);
  const [winnerOverlayAccentColor, setWinnerOverlayAccentColor] = useState("");
  const [winnerOverlayIcon, setWinnerOverlayIcon] = useState("trophy");
  const [winnerOverlayIconUrl, setWinnerOverlayIconUrl] = useState("");
  const [winnerOverlayShowConfetti, setWinnerOverlayShowConfetti] = useState(true);
  const [winnerOverlayShowPoints, setWinnerOverlayShowPoints] = useState(true);
  const [winnerOverlayVisible, setWinnerOverlayVisible] = useState(false);
  // 12-points overlay custom style
  const [overlay12StyleCustom, setOverlay12StyleCustom] = useState(false);
  const [overlay12FontTitle, setOverlay12FontTitle] = useState("Unbounded");
  const [overlay12FontCountry, setOverlay12FontCountry] = useState("Inter");
  const [overlay12FontSecondary, setOverlay12FontSecondary] = useState("Inter");
  const [overlay12SizeScale, setOverlay12SizeScale] = useState("m");
  // Winner overlay custom style
  const [winnerOverlayStyleCustom, setWinnerOverlayStyleCustom] = useState(false);
  const [winnerOverlayFontTitle, setWinnerOverlayFontTitle] = useState("Unbounded");
  const [winnerOverlayFontCountry, setWinnerOverlayFontCountry] = useState("Inter");
  const [winnerOverlayFontSecondary, setWinnerOverlayFontSecondary] = useState("Inter");
  const [winnerOverlaySizeScale, setWinnerOverlaySizeScale] = useState("m");
  const [loading, setLoading] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  const adminInitRef = useRef(null);
  const adminPollRef = useRef(null);

  useEffect(() => {
    if (adminInitRef.current) adminInitRef.current();
    const interval = setInterval(() => {
      if (adminPollRef.current) adminPollRef.current();
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const fetchCurrentVoterStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/current-voter`);
      setOverlay12Visible(response.data.twelve_overlay_visible === true);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error("current-voter poll failed:", e);
    }
    try {
      const winnerRes = await axios.get(`${API}/winner-overlay`);
      setWinnerOverlayVisible(winnerRes.data.visible === true);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error("winner-overlay poll failed:", e);
    }
  };

  const handleHideOverlay12 = async () => {
    try {
      await axios.post(`${API}/admin/hide-12-overlay`);
      setOverlay12Visible(false);
      toast.success("Overlay ocultado");
    } catch (e) {
      toast.error("Error al ocultar el overlay");
    }
  };

  const handlePreviewOverlay12 = async () => {
    if (!previewGiverId || !previewRecipientId) {
      toast.error("Selecciona el país que otorga y el receptor");
      return;
    }
    if (previewGiverId === previewRecipientId) {
      toast.error("El país que otorga y el receptor deben ser diferentes");
      return;
    }
    try {
      const res = await axios.post(`${API}/admin/preview-12-overlay`, {
        giver_id: previewGiverId,
        recipient_id: previewRecipientId
      });
      toast.success(`Ensayo: ${res.data.giver} → ${res.data.recipient}`);
    } catch (e) {
      toast.error("Error al previsualizar el overlay");
    }
  };

  const [winnerPreviewId, setWinnerPreviewId] = useState("");
  const [top6OverlayVisible, setTop6OverlayVisible] = useState(false);

  const handleShowTop6 = async () => {
    try {
      await axios.post(`${API}/admin/show-top6-overlay`);
      setTop6OverlayVisible(true);
      toast.success("Mostrando Top 6 actual en el visualizador");
    } catch (e) {
      toast.error("Error al mostrar el Top 6");
    }
  };

  const handleHideTop6 = async () => {
    try {
      await axios.post(`${API}/admin/hide-top6-overlay`);
      setTop6OverlayVisible(false);
      toast.success("Overlay del Top 6 ocultado");
    } catch (e) {
      toast.error("Error al ocultar el Top 6");
    }
  };

  const handleShowWinner = async (winnerId = null) => {
    try {
      const res = await axios.post(`${API}/admin/show-winner-overlay`, {
        winner_id: winnerId
      });
      const winnerCountry = countries.find(c => c.id === res.data.winner_id);
      toast.success(`Mostrando ganador: ${winnerCountry?.name || 'desconocido'} (${res.data.winner_points} pts)`);
    } catch (e) {
      toast.error("Error al mostrar el ganador");
    }
  };

  const handleHideWinner = async () => {
    try {
      await axios.post(`${API}/admin/hide-winner-overlay`);
      setWinnerOverlayVisible(false);
      toast.success("Overlay del ganador ocultado");
    } catch (e) {
      toast.error("Error al ocultar el overlay del ganador");
    }
  };

  // Helper function to get font format from URL
  const getFontFormat = (url) => {
    if (url.endsWith('.woff2')) return 'woff2';
    if (url.endsWith('.woff')) return 'woff';
    if (url.endsWith('.ttf')) return 'truetype';
    return 'opentype';
  };

  // Load custom fonts for each section when they change
  useEffect(() => {
    const fontsToLoad = [
      { url: customFontTitleUrl, name: customFontTitleName, id: 'custom-font-title' },
      { url: customFontSubtitleUrl, name: customFontSubtitleName, id: 'custom-font-subtitle' },
      { url: customFontCountryUrl, name: customFontCountryName, id: 'custom-font-country' },
      { url: customFontPointsUrl, name: customFontPointsName, id: 'custom-font-points' },
      { url: customFontVotingUrl, name: customFontVotingName, id: 'custom-font-voting' },
    ];

    fontsToLoad.forEach(({ url, name, id }) => {
      if (url && name) {
        const resolvedFontUrl = resolveUrl(url);
        const style = document.createElement('style');
        style.id = id;
        style.textContent = `
          @font-face {
            font-family: '${name}';
            src: url('${resolvedFontUrl}') format('${getFontFormat(url)}');
            font-weight: normal;
            font-style: normal;
          }
        `;
        const oldStyle = document.getElementById(id);
        if (oldStyle) oldStyle.remove();
        document.head.appendChild(style);
      }
    });
  }, [customFontTitleUrl, customFontTitleName, customFontSubtitleUrl, customFontSubtitleName, customFontCountryUrl, customFontCountryName, customFontPointsUrl, customFontPointsName, customFontVotingUrl, customFontVotingName]);

  const fetchBackground = async () => {
    try {
      const response = await axios.get(`${API}/background`);
      setBackgroundVideo(response.data.video_url || "");
      setBgVideoBlur(response.data.bg_video_blur ?? 10);
      setBgVideoBrightness(response.data.bg_video_brightness ?? 100);
      setBgVideoOpacity(response.data.bg_video_opacity ?? 20);
      setFontTitle(response.data.font_title || "Unbounded");
      setFontSubtitle(response.data.font_subtitle || "Inter");
      setFontCountry(response.data.font_country || "Inter");
      setFontPoints(response.data.font_points || "Chivo Mono");
      setFontVoting(response.data.font_voting || "Inter");
      setFontTitleItalic(response.data.font_title_italic === true);
      setFontSubtitleItalic(response.data.font_subtitle_italic === true);
      setFontCountryItalic(response.data.font_country_italic === true);
      setFontPointsItalic(response.data.font_points_italic === true);
      setFontVotingItalic(response.data.font_voting_italic === true);
      setFontVotingSecondaryItalic(response.data.font_voting_secondary_italic === true);
      // Custom fonts for each section
      setCustomFontTitleUrl(response.data.custom_font_title_url || "");
      setCustomFontTitleName(response.data.custom_font_title_name || "");
      setCustomFontSubtitleUrl(response.data.custom_font_subtitle_url || "");
      setCustomFontSubtitleName(response.data.custom_font_subtitle_name || "");
      setCustomFontCountryUrl(response.data.custom_font_country_url || "");
      setCustomFontCountryName(response.data.custom_font_country_name || "");
      setCustomFontPointsUrl(response.data.custom_font_points_url || "");
      setCustomFontPointsName(response.data.custom_font_points_name || "");
      setCustomFontVotingUrl(response.data.custom_font_voting_url || "");
      setCustomFontVotingName(response.data.custom_font_voting_name || "");
      // Font sizes
      setFontSizeTitle(response.data.font_size_title || 48);
      setFontSizeSubtitle(response.data.font_size_subtitle || 16);
      setFontSizeCountry(response.data.font_size_country || 14);
      setFontSizePoints(response.data.font_size_points || 24);
      setFontSizeVoting(response.data.font_size_voting || 20);
      // Colors
      setPrimaryColor(response.data.primary_color || "#EC4899");
      setSecondaryColor(response.data.secondary_color || "#22D3EE");
      setTertiaryColor(response.data.tertiary_color || "#8B5CF6");
      // Row colors
      setRowBgEven(response.data.row_bg_even || "rgba(24, 24, 27, 0.9)");
      setRowBgOdd(response.data.row_bg_odd || "rgba(39, 39, 42, 0.7)");
      setRowBgEvenTranslucent(response.data.row_bg_even_translucent === true);
      setRowBgOddTranslucent(response.data.row_bg_odd_translucent === true);
      setRowBadgeTranslucent(response.data.row_badge_translucent === true);
      setLogoUrl(response.data.logo_url || "");
      setLogoPosition(response.data.logo_position || "top-left");
      setLogoSize(response.data.logo_size || 80);
      setLogoZIndex(response.data.logo_z_index ?? 50);
      setLogoOpacity(response.data.logo_opacity ?? 100);
      setLogoX(response.data.logo_x ?? null);
      setLogoY(response.data.logo_y ?? null);
      setExtraImage1Url(response.data.extra_image_1_url || "");
      setExtraImage1Position(response.data.extra_image_1_position || "top-right");
      setExtraImage1Size(response.data.extra_image_1_size || 80);
      setExtraImage1ZIndex(response.data.extra_image_1_z_index ?? 50);
      setExtraImage1Opacity(response.data.extra_image_1_opacity ?? 100);
      setExtraImage1X(response.data.extra_image_1_x ?? null);
      setExtraImage1Y(response.data.extra_image_1_y ?? null);
      setExtraImage2Url(response.data.extra_image_2_url || "");
      setExtraImage2Position(response.data.extra_image_2_position || "bottom-left");
      setExtraImage2Size(response.data.extra_image_2_size || 80);
      setExtraImage2ZIndex(response.data.extra_image_2_z_index ?? 50);
      setExtraImage2Opacity(response.data.extra_image_2_opacity ?? 100);
      setExtraImage2X(response.data.extra_image_2_x ?? null);
      setExtraImage2Y(response.data.extra_image_2_y ?? null);
      setExtraImage3Url(response.data.extra_image_3_url || "");
      setExtraImage3Position(response.data.extra_image_3_position || "bottom-right");
      setExtraImage3Size(response.data.extra_image_3_size || 80);
      setExtraImage3ZIndex(response.data.extra_image_3_z_index ?? 50);
      setExtraImage3Opacity(response.data.extra_image_3_opacity ?? 100);
      setExtraImage3X(response.data.extra_image_3_x ?? null);
      setExtraImage3Y(response.data.extra_image_3_y ?? null);
      setDefaultArtistPhoto(response.data.default_artist_photo || "");
      setArtistPhotoSizeMode(response.data.artist_photo_size_mode || "adaptive");
      setArtistPhotoSizePx(response.data.artist_photo_size_px || 300);
      // Texts
      setTextTitle(response.data.text_title || "CLASIFICACIÓN EN VIVO");
      setTextSubtitle(response.data.text_subtitle || "Sistema de Votación Eurovisión");
      setTextVotingNow(response.data.text_voting_now || "VOTANDO AHORA");
      setTextWaiting(response.data.text_waiting || "Esperando próxima votación...");
      // Sound
      setSound12PointsUrl(response.data.sound_12_points_url || "");
      setSoundWinnerUrl(response.data.sound_winner_url || "");
      // Confetti
      setConfettiEnabled(response.data.confetti_enabled !== false);
      setConfettiColors(response.data.confetti_colors || ["#EC4899", "#22D3EE", "#8B5CF6", "#FFD700", "#FF6B6B"]);
      setTop1Color(response.data.top1_color || "#EC4899");
      setTop2Color(response.data.top2_color || "#8B5CF6");
      setTop3Color(response.data.top3_color || "#22D3EE");
      setLastPlaceColor(response.data.last_place_color || "#8B2020");
      setTop1Translucent(response.data.top1_translucent !== false);
      setTop2Translucent(response.data.top2_translucent !== false);
      setTop3Translucent(response.data.top3_translucent !== false);
      setLastPlaceTranslucent(response.data.last_place_translucent === true);
      setPositionBadgeTranslucent(response.data.position_badge_translucent === true);
      // Scoreboard layout
      setVotingColumnCenter(response.data.voting_column_center === true);
      setLeftColSpan(response.data.left_column_span || 4);
      setRightColSpan(response.data.right_column_span || 4);
      setVotingColSpan(response.data.voting_column_span || 4);
      setColumnGap(response.data.column_gap ?? 6);
      setVotingWidthCustom(response.data.voting_width_custom === true);
      setVotingWidthSpan(response.data.voting_width_span ?? 4);
      setVoteBarColorMode(response.data.vote_bar_color_mode || "original");
      setVoteBarColor1(response.data.vote_bar_color_1 || "#18181b");
      setVoteBarColor2(response.data.vote_bar_color_2 || "#27272a");
      setVoteBarTranslucent(response.data.vote_bar_translucent === true);
      setVoteBarOpacity(response.data.vote_bar_opacity ?? 100);
      // Subtitle counter
      setSubtitleCounterEnabled(response.data.subtitle_counter_enabled === true);
      setSubtitlePhaseOrder(response.data.subtitle_phase_order || "jury_first");
      setSubtitleJuryLabel(response.data.subtitle_jury_label || "Jurado Profesional");
      setSubtitleTelevoteLabel(response.data.subtitle_televote_label || "Televoto");
      setMystery12Text(response.data.mystery_12_text || "?????????");
      setMysteryHiddenPoints(response.data.mystery_hidden_points || "12");
      setOverlay12CustomFontUrl(response.data.overlay_12_custom_font_url || "");
      setWinnerCustomFontUrl(response.data.winner_overlay_custom_font_url || "");
      setTop6TitleText(response.data.top6_title_text || "Current top 6:");
      setTop6StyleCustom(response.data.top6_style_custom || false);
      setTop6FontTitle(response.data.top6_font_title || "Unbounded");
      setTop6FontCountry(response.data.top6_font_country || "Inter");
      setVoteDisplayColumns(response.data.vote_display_columns || "single");
      setShowVoterBorder(response.data.show_voter_border !== false);
      setShowJuryBadge(response.data.show_jury_badge !== false);
      setFontVotingSecondary(response.data.font_voting_secondary || "Inter");
      setCustomFontVotingSecondaryUrl(response.data.custom_font_voting_secondary_url || "");
      setCustomFontVotingSecondaryName(response.data.custom_font_voting_secondary_name || "");
      setOverlay12Enabled(response.data.overlay_12_enabled !== false);
      setOverlay12DurationMode(response.data.overlay_12_duration_mode || "auto");
      setOverlay12DurationSeconds(response.data.overlay_12_duration_seconds ?? 8);
      setOverlay12BgStyle(response.data.overlay_12_bg_style || "gradient");
      setOverlay12BgColor(response.data.overlay_12_bg_color || "#000000");
      setOverlay12BgOpacity(response.data.overlay_12_bg_opacity ?? 80);
      setOverlay12TextReceives(response.data.overlay_12_text_receives || "OTORGA 12 PUNTOS A");
      setOverlay12UseAdminColors(response.data.overlay_12_use_admin_colors !== false);
      setOverlay12ArrowColor(response.data.overlay_12_arrow_color || "");
      setOverlay12ArrowThickness(response.data.overlay_12_arrow_thickness ?? 12);
      setOverlay12ArrowHeadSize(response.data.overlay_12_arrow_head_size ?? 46);
      setOverlay12ShowConfetti(response.data.overlay_12_show_confetti !== false);
      // Winner overlay
      setWinnerOverlayEnabled(response.data.winner_overlay_enabled !== false);
      setWinnerOverlayDurationMode(response.data.winner_overlay_duration_mode || "manual");
      setWinnerOverlayDurationSeconds(response.data.winner_overlay_duration_seconds ?? 15);
      setWinnerOverlayBgStyle(response.data.winner_overlay_bg_style || "gradient");
      setWinnerOverlayBgColor(response.data.winner_overlay_bg_color || "#000000");
      setWinnerOverlayBgOpacity(response.data.winner_overlay_bg_opacity ?? 90);
      setWinnerOverlayTitleText(response.data.winner_overlay_title_text || "¡ENHORABUENA!");
      setWinnerOverlaySubtitleText(response.data.winner_overlay_subtitle_text || "GANADOR");
      setWinnerOverlayUseAdminColors(response.data.winner_overlay_use_admin_colors !== false);
      setWinnerOverlayAccentColor(response.data.winner_overlay_accent_color || "");
      setWinnerOverlayIcon(response.data.winner_overlay_icon || "trophy");
      setWinnerOverlayIconUrl(response.data.winner_overlay_icon_url || "");
      setWinnerOverlayShowConfetti(response.data.winner_overlay_show_confetti !== false);
      setWinnerOverlayShowPoints(response.data.winner_overlay_show_points !== false);
      setOverlay12StyleCustom(response.data.overlay_12_style_custom === true);
      setOverlay12FontTitle(response.data.overlay_12_font_title || "Unbounded");
      setOverlay12FontCountry(response.data.overlay_12_font_country || "Inter");
      setOverlay12FontSecondary(response.data.overlay_12_font_secondary || "Inter");
      setOverlay12SizeScale(response.data.overlay_12_size_scale || "m");
      setWinnerOverlayStyleCustom(response.data.winner_overlay_style_custom === true);
      setWinnerOverlayFontTitle(response.data.winner_overlay_font_title || "Unbounded");
      setWinnerOverlayFontCountry(response.data.winner_overlay_font_country || "Inter");
      setWinnerOverlayFontSecondary(response.data.winner_overlay_font_secondary || "Inter");
      setWinnerOverlaySizeScale(response.data.winner_overlay_size_scale || "m");
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Error fetching background", error);
    }
  };

  const handleSetBackground = async () => {
    try {
      await axios.post(`${API}/admin/background`, { 
        video_url: backgroundVideo,
        bg_video_blur: bgVideoBlur,
        bg_video_brightness: bgVideoBrightness,
        bg_video_opacity: bgVideoOpacity,
        font_title: fontTitle,
        font_subtitle: fontSubtitle,
        font_country: fontCountry,
        font_points: fontPoints,
        font_voting: fontVoting,
        font_title_italic: fontTitleItalic,
        font_subtitle_italic: fontSubtitleItalic,
        font_country_italic: fontCountryItalic,
        font_points_italic: fontPointsItalic,
        font_voting_italic: fontVotingItalic,
        font_voting_secondary_italic: fontVotingSecondaryItalic,
        custom_font_title_url: customFontTitleUrl,
        custom_font_title_name: customFontTitleName,
        custom_font_subtitle_url: customFontSubtitleUrl,
        custom_font_subtitle_name: customFontSubtitleName,
        custom_font_country_url: customFontCountryUrl,
        custom_font_country_name: customFontCountryName,
        custom_font_points_url: customFontPointsUrl,
        custom_font_points_name: customFontPointsName,
        custom_font_voting_url: customFontVotingUrl,
        custom_font_voting_name: customFontVotingName,
        font_size_title: fontSizeTitle,
        font_size_subtitle: fontSizeSubtitle,
        font_size_country: fontSizeCountry,
        font_size_points: fontSizePoints,
        font_size_voting: fontSizeVoting,
        primary_color: primaryColor,
        secondary_color: secondaryColor,
        tertiary_color: tertiaryColor,
        row_bg_even: rowBgEven,
        row_bg_odd: rowBgOdd,
        row_bg_even_translucent: rowBgEvenTranslucent,
        row_bg_odd_translucent: rowBgOddTranslucent,
        row_badge_translucent: rowBadgeTranslucent,
        logo_url: logoUrl,
        logo_position: logoPosition,
        logo_size: logoSize,
        logo_z_index: logoZIndex,
        logo_opacity: logoOpacity,
        logo_x: logoX,
        logo_y: logoY,
        extra_image_1_url: extraImage1Url,
        extra_image_1_position: extraImage1Position,
        extra_image_1_size: extraImage1Size,
        extra_image_1_z_index: extraImage1ZIndex,
        extra_image_1_opacity: extraImage1Opacity,
        extra_image_1_x: extraImage1X,
        extra_image_1_y: extraImage1Y,
        extra_image_2_url: extraImage2Url,
        extra_image_2_position: extraImage2Position,
        extra_image_2_size: extraImage2Size,
        extra_image_2_z_index: extraImage2ZIndex,
        extra_image_2_opacity: extraImage2Opacity,
        extra_image_2_x: extraImage2X,
        extra_image_2_y: extraImage2Y,
        extra_image_3_url: extraImage3Url,
        extra_image_3_position: extraImage3Position,
        extra_image_3_size: extraImage3Size,
        extra_image_3_z_index: extraImage3ZIndex,
        extra_image_3_opacity: extraImage3Opacity,
        extra_image_3_x: extraImage3X,
        extra_image_3_y: extraImage3Y,
        default_artist_photo: defaultArtistPhoto,
        artist_photo_size_mode: artistPhotoSizeMode,
        artist_photo_size_px: artistPhotoSizePx,
        text_title: textTitle,
        text_subtitle: textSubtitle,
        text_voting_now: textVotingNow,
        text_waiting: textWaiting,
        sound_12_points_url: sound12PointsUrl,
        sound_winner_url: soundWinnerUrl,
        confetti_enabled: confettiEnabled,
        confetti_colors: confettiColors,
        top1_color: top1Color,
        top2_color: top2Color,
        top3_color: top3Color,
        last_place_color: lastPlaceColor,
        top1_translucent: top1Translucent,
        top2_translucent: top2Translucent,
        top3_translucent: top3Translucent,
        last_place_translucent: lastPlaceTranslucent,
        position_badge_translucent: positionBadgeTranslucent,
        voting_column_center: votingColumnCenter,
        left_column_span: leftColSpan,
        right_column_span: rightColSpan,
        voting_column_span: votingColSpan,
        column_gap: columnGap,
        voting_width_custom: votingWidthCustom,
        voting_width_span: votingWidthSpan,
        vote_bar_color_mode: voteBarColorMode,
        vote_bar_color_1: voteBarColor1,
        vote_bar_color_2: voteBarColor2,
        vote_bar_translucent: voteBarTranslucent,
        vote_bar_opacity: voteBarOpacity,
        subtitle_counter_enabled: subtitleCounterEnabled,
        subtitle_phase_order: subtitlePhaseOrder,
        subtitle_jury_label: subtitleJuryLabel,
        subtitle_televote_label: subtitleTelevoteLabel,
        mystery_12_text: mystery12Text,
        mystery_hidden_points: mysteryHiddenPoints,
        overlay_12_custom_font_url: overlay12CustomFontUrl,
        winner_overlay_custom_font_url: winnerCustomFontUrl,
        top6_title_text: top6TitleText,
        top6_style_custom: top6StyleCustom,
        top6_font_title: top6FontTitle,
        top6_font_country: top6FontCountry,
        vote_display_columns: voteDisplayColumns,
        show_voter_border: showVoterBorder,
        show_jury_badge: showJuryBadge,
        font_voting_secondary: fontVotingSecondary,
        custom_font_voting_secondary_url: customFontVotingSecondaryUrl,
        custom_font_voting_secondary_name: customFontVotingSecondaryName,
        overlay_12_enabled: overlay12Enabled,
        overlay_12_duration_mode: overlay12DurationMode,
        overlay_12_duration_seconds: overlay12DurationSeconds,
        overlay_12_bg_style: overlay12BgStyle,
        overlay_12_bg_color: overlay12BgColor,
        overlay_12_bg_opacity: overlay12BgOpacity,
        overlay_12_text_receives: overlay12TextReceives,
        overlay_12_use_admin_colors: overlay12UseAdminColors,
        overlay_12_arrow_color: overlay12ArrowColor,
        overlay_12_arrow_thickness: overlay12ArrowThickness,
        overlay_12_arrow_head_size: overlay12ArrowHeadSize,
        overlay_12_show_confetti: overlay12ShowConfetti,
        winner_overlay_enabled: winnerOverlayEnabled,
        winner_overlay_duration_mode: winnerOverlayDurationMode,
        winner_overlay_duration_seconds: winnerOverlayDurationSeconds,
        winner_overlay_bg_style: winnerOverlayBgStyle,
        winner_overlay_bg_color: winnerOverlayBgColor,
        winner_overlay_bg_opacity: winnerOverlayBgOpacity,
        winner_overlay_title_text: winnerOverlayTitleText,
        winner_overlay_subtitle_text: winnerOverlaySubtitleText,
        winner_overlay_use_admin_colors: winnerOverlayUseAdminColors,
        winner_overlay_accent_color: winnerOverlayAccentColor,
        winner_overlay_icon: winnerOverlayIcon,
        winner_overlay_icon_url: winnerOverlayIconUrl,
        winner_overlay_show_confetti: winnerOverlayShowConfetti,
        winner_overlay_show_points: winnerOverlayShowPoints,
        overlay_12_style_custom: overlay12StyleCustom,
        overlay_12_font_title: overlay12FontTitle,
        overlay_12_font_country: overlay12FontCountry,
        overlay_12_font_secondary: overlay12FontSecondary,
        overlay_12_size_scale: overlay12SizeScale,
        winner_overlay_style_custom: winnerOverlayStyleCustom,
        winner_overlay_font_title: winnerOverlayFontTitle,
        winner_overlay_font_country: winnerOverlayFontCountry,
        winner_overlay_font_secondary: winnerOverlayFontSecondary,
        winner_overlay_size_scale: winnerOverlaySizeScale
      });
      toast.success("Configuración aplicada correctamente");
    } catch (error) {
      toast.error("Error al configurar");
    }
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const response = await axios.get(`${API}/admin/export-pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `resultados_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF exportado correctamente");
    } catch (error) {
      toast.error("Error al exportar PDF");
    } finally {
      setExportingPdf(false);
    }
  };

  const handleFileUpload = async (file, field) => {
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const uploadedUrl = `${BACKEND_URL}${response.data.url}`;
      
      if (field === 'video') setBackgroundVideo(uploadedUrl);
      if (field === 'logo') setLogoUrl(uploadedUrl);
      if (field === 'defaultArtist') setDefaultArtistPhoto(uploadedUrl);
      if (field === 'sound12') setSound12PointsUrl(uploadedUrl);
      if (field === 'soundWinner') setSoundWinnerUrl(uploadedUrl);
      
      toast.success("Archivo subido correctamente");
      return uploadedUrl;
    } catch (error) {
      toast.error("Error al subir archivo");
      return null;
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API}/admin/countries`);
      setCountries(response.data);
    } catch (error) {
      toast.error("Error al cargar países");
    }
  };

  const fetchVotes = async () => {
    try {
      const response = await axios.get(`${API}/admin/votes`);
      setVotes(response.data);
    } catch (error) {
      toast.error("Error al cargar votos");
    }
  };

  // ---------------- Multi-event handlers ----------------
  // Robust same-origin download via blob (no new tab, honors filename)
  const downloadFromApi = async (path, fallbackName, loadingMsg) => {
    try {
      if (loadingMsg) toast.message(loadingMsg);
      const response = await axios.get(`${API}${path}`, { responseType: 'blob' });
      let filename = fallbackName;
      const cd = response.headers['content-disposition'];
      if (cd) {
        const m = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)/i);
        if (m) filename = decodeURIComponent(m[1]);
      }
      const blob = new Blob([response.data], { type: response.data.type || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1500);
      toast.success("Descarga lista");
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error("Download failed:", path, e);
      toast.error("Error al generar el archivo");
    }
  };

  const handleExportVotesPdf = () => {
    downloadFromApi('/admin/export-votes-pdf', 'votos_emitidos.pdf', 'Generando PDF de votos...');
  };

  const handleExportVotesExcel = () => {
    downloadFromApi('/admin/export-votes-excel', 'votos_emitidos.xlsx', 'Generando Excel de votos...');
  };

  const fetchEvents = async () => {
    try {
      const [evRes, activeRes] = await Promise.all([
        axios.get(`${API}/events`),
        axios.get(`${API}/events/active`)
      ]);
      setEvents(evRes.data || []);
      setActiveEvent(activeRes.data || null);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error("fetchEvents failed:", e);
    }
  };

  const reloadEventData = async () => {
    await Promise.all([
      fetchCountries(),
      fetchVotes(),
      fetchBackground(),
      fetchCurrentVoterStatus()
    ]);
  };

  const handleSelectEvent = async (value) => {
    if (value === "__create__") {
      setNewEventName("");
      setCopyFromEventId("none");
      setShowCreateEvent(true);
      return;
    }
    if (activeEvent && value === activeEvent.id) return;
    try {
      await axios.post(`${API}/events/active`, { event_id: value });
      await fetchEvents();
      await reloadEventData();
      toast.success("Evento cambiado");
    } catch (e) {
      toast.error("Error al cambiar de evento");
    }
  };

  const handleCreateEvent = async () => {
    if (!newEventName.trim()) {
      toast.error("Escribe un nombre para el evento");
      return;
    }
    setCreatingEvent(true);
    try {
      await axios.post(`${API}/events`, {
        name: newEventName.trim(),
        copy_from_event_id: copyFromEventId === "none" ? null : copyFromEventId
      });
      setShowCreateEvent(false);
      await fetchEvents();
      await reloadEventData();
      toast.success("Evento creado");
    } catch (e) {
      toast.error("Error al crear el evento");
    } finally {
      setCreatingEvent(false);
    }
  };

  const handleRenameEvent = async () => {
    if (!renameEventName.trim() || !activeEvent) return;
    try {
      await axios.put(`${API}/events/${activeEvent.id}`, { name: renameEventName.trim() });
      setShowRenameEvent(false);
      await fetchEvents();
      toast.success("Nombre actualizado");
    } catch (e) {
      toast.error("Error al renombrar el evento");
    }
  };

  const handleDeleteEvent = async () => {
    if (!activeEvent) return;
    if (events.length <= 1) {
      toast.error("No puedes eliminar el único evento existente");
      return;
    }
    if (deleteEventConfirmText.trim() !== activeEvent.name) {
      toast.error("El nombre no coincide");
      return;
    }
    setDeletingEvent(true);
    try {
      await axios.delete(`${API}/events/${activeEvent.id}`);
      setShowDeleteEvent(false);
      setDeleteEventConfirmText("");
      await fetchEvents();
      await reloadEventData();
      toast.success("Evento eliminado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar el evento");
    } finally {
      setDeletingEvent(false);
    }
  };

  const handleExportEvent = () => {
    const safeName = (activeEvent?.name || 'evento').replace(/\s+/g, '_');
    downloadFromApi('/admin/export-event', `evento_${safeName}.json`, 'Generando archivo del evento...');
  };

  const handleImportEvent = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      await axios.post(`${API}/admin/import-event`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setShowImportConfirm(false);
      setImportFile(null);
      await reloadEventData();
      toast.success("Datos importados correctamente");
    } catch (e) {
      toast.error("Error al importar (archivo inválido)");
    } finally {
      setImporting(false);
    }
  };

  const handleAddCountry = async (e) => {
    e.preventDefault();
    if (!newCountry.name || !newCountry.password) {
      toast.error("Nombre y contraseña son requeridos");
      return;
    }

    setLoading(true);
    try {
      const flagUrl = newCountry.flag_url || `https://flagcdn.com/w320/${newCountry.name.toLowerCase().substring(0, 2)}.png`;
      const artistUrl = newCountry.artist_photo_url || "https://placehold.co/400x400/222/FFF?text=Artist";
      
      await axios.post(`${API}/admin/countries`, {
        ...newCountry,
        flag_url: flagUrl,
        artist_photo_url: artistUrl,
        running_order: parseInt(newCountry.running_order, 10) || 0
      });
      
      toast.success(`${newCountry.is_jury ? 'Jurado' : 'País'} agregado correctamente`);
      setNewCountry({ name: "", password: "", flag_url: "", artist_photo_url: "", artist_name: "", song_title: "", overlay_photo_url: "", is_jury: false, running_order: 0 });
      fetchCountries();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al agregar");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCountry = async (id, name) => {
    if (!window.confirm(`¿Eliminar ${name}?`)) return;
    
    try {
      await axios.delete(`${API}/admin/countries/${id}`);
      toast.success("Eliminado correctamente");
      fetchCountries();
      fetchVotes();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const handleSetCurrentVoter = async (countryId, countryName) => {
    try {
      await axios.post(`${API}/admin/current-voter`, { country_id: countryId });
      toast.success(`Mostrando votos de ${countryName}`);
    } catch (error) {
      toast.error("Error al establecer votante");
    }
  };

  const handleResetVotes = async () => {
    if (!window.confirm("¿Resetear TODOS los votos? Esta acción no se puede deshacer.")) return;
    
    try {
      await axios.post(`${API}/admin/reset-votes`);
      toast.success("Votos reseteados correctamente");
      fetchVotes();
    } catch (error) {
      toast.error("Error al resetear votos");
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/admin/logout`);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error("logout failed:", e);
    }
    localStorage.removeItem('admin_token');
    toast.success("Sesión cerrada");
    navigate('/admin-login');
  };

  const handleApplyVotes = async (countryId, countryName) => {
    try {
      await axios.post(`${API}/admin/apply-votes/${countryId}`);
      toast.success(`Puntos de ${countryName} sumados al total!`);
      fetchVotes();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.warning("Los votos de este país ya fueron sumados");
      } else {
        toast.error("Error al sumar votos");
      }
    }
  };

  const handleUnapplyAllVotes = async () => {
    try {
      const res = await axios.post(`${API}/admin/unapply-all-votes`);
      toast.success("Votos des-sumados: el marcador vuelve a cero. Los votos recibidos se conservan.");
      await Promise.all([fetchVotes(), fetchCountries()]);
      return res;
    } catch (error) {
      toast.error("Error al des-sumar los votos");
    }
  };

  const handleReveal12Points = async (countryName) => {
    try {
      await axios.post(`${API}/admin/reveal-12-points`);
      toast.success(`12 puntos de ${countryName} revelados!`);
    } catch (error) {
      toast.error("Error al revelar los 12 puntos");
    }
  };

  const handleEditCountry = (country) => {
    setEditingCountry({ ...country });
  };

  const handleUpdateCountry = async () => {
    if (!editingCountry.name || !editingCountry.password) {
      toast.error("Nombre y contraseña son requeridos");
      return;
    }

    setLoading(true);
    try {
      await axios.put(`${API}/admin/countries/${editingCountry.id}`, {
        name: editingCountry.name,
        password: editingCountry.password,
        flag_url: editingCountry.flag_url,
        artist_photo_url: editingCountry.artist_photo_url,
        artist_name: editingCountry.artist_name || "",
        song_title: editingCountry.song_title || "",
        overlay_photo_url: editingCountry.overlay_photo_url || "",
        running_order: parseInt(editingCountry.running_order, 10) || 0
      });
      toast.success("País actualizado correctamente");
      setEditingCountry(null);
      fetchCountries();
    } catch (error) {
      toast.error("Error al actualizar país");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleBadge = async (countryId) => {
    try {
      const response = await axios.patch(`${API}/admin/countries/${countryId}/badge`);
      toast.success(`Badge ${response.data.show_position_badge ? 'activado' : 'desactivado'}`);
      fetchCountries();
    } catch (error) {
      toast.error("Error al cambiar badge");
    }
  };

  const handleToggleTranslucent = async (countryId) => {
    try {
      const response = await axios.patch(`${API}/admin/countries/${countryId}/translucent`);
      toast.success(`Barra ${response.data.bar_translucent ? 'translúcida' : 'opaca'}`);
      fetchCountries();
    } catch (error) {
      toast.error("Error al cambiar opacidad");
    }
  };

  const handleResetAllBarColors = async () => {
    try {
      const response = await axios.post(`${API}/admin/reset-bar-colors`);
      toast.success(`Colores de barras restablecidos (${response.data.reset_count})`);
      fetchCountries();
    } catch (error) {
      toast.error("Error al restablecer colores");
    }
  };



  const votingCountries = countries.filter(c => !c.is_jury);
  const juryMembers = countries.filter(c => c.is_jury);
  const votedCountries = votes.map(v => v.from_country_id);

  // Keep refs to the latest fetch closures so the mount/polling effect deps stay stable.
  adminInitRef.current = () => {
    fetchEvents();
    fetchCountries();
    fetchVotes();
    fetchBackground();
    fetchCurrentVoterStatus();
  };
  adminPollRef.current = fetchCurrentVoterStatus;

  const adminCtx = {
    countries, setCountries, votes, setVotes, events, setEvents,
    activeEvent, setActiveEvent, showCreateEvent, setShowCreateEvent, newEventName, setNewEventName,
    copyFromEventId, setCopyFromEventId, creatingEvent, setCreatingEvent, showRenameEvent, setShowRenameEvent,
    renameEventName, setRenameEventName, importFile, setImportFile, showImportConfirm, setShowImportConfirm,
    importing, setImporting, newCountry, setNewCountry, editingCountry, setEditingCountry,
    backgroundVideo, setBackgroundVideo, bgVideoBlur, setBgVideoBlur, bgVideoBrightness, setBgVideoBrightness, bgVideoOpacity, setBgVideoOpacity, fontTitle, setFontTitle, fontSubtitle, setFontSubtitle,
    fontCountry, setFontCountry, fontPoints, setFontPoints, fontVoting, setFontVoting,
    customFontTitleUrl, setCustomFontTitleUrl, customFontTitleName, setCustomFontTitleName, customFontSubtitleUrl, setCustomFontSubtitleUrl,
    customFontSubtitleName, setCustomFontSubtitleName, customFontCountryUrl, setCustomFontCountryUrl, customFontCountryName, setCustomFontCountryName,
    customFontPointsUrl, setCustomFontPointsUrl, customFontPointsName, setCustomFontPointsName, customFontVotingUrl, setCustomFontVotingUrl,
    customFontVotingName, setCustomFontVotingName, fontSizeTitle, setFontSizeTitle, fontSizeSubtitle, setFontSizeSubtitle,
    fontSizeCountry, setFontSizeCountry, fontSizePoints, setFontSizePoints, fontSizeVoting, setFontSizeVoting,
    primaryColor, setPrimaryColor, secondaryColor, setSecondaryColor, tertiaryColor, setTertiaryColor,
    rowBgEven, setRowBgEven, rowBgOdd, setRowBgOdd, rowBgEvenTranslucent, setRowBgEvenTranslucent, rowBgOddTranslucent, setRowBgOddTranslucent, rowBadgeTranslucent, setRowBadgeTranslucent, logoUrl, setLogoUrl,
    logoPosition, setLogoPosition, logoSize, setLogoSize, logoZIndex, setLogoZIndex,
    logoOpacity, setLogoOpacity, logoX, setLogoX, logoY, setLogoY, extraImage1Url, setExtraImage1Url, extraImage1Position, setExtraImage1Position,
    extraImage1Size, setExtraImage1Size, extraImage1ZIndex, setExtraImage1ZIndex, extraImage1Opacity, setExtraImage1Opacity,
    extraImage1X, setExtraImage1X, extraImage1Y, setExtraImage1Y,
    extraImage2Url, setExtraImage2Url, extraImage2Position, setExtraImage2Position, extraImage2Size, setExtraImage2Size,
    extraImage2ZIndex, setExtraImage2ZIndex, extraImage2Opacity, setExtraImage2Opacity, extraImage3Url, setExtraImage3Url,
    extraImage2X, setExtraImage2X, extraImage2Y, setExtraImage2Y,
    extraImage3Position, setExtraImage3Position, extraImage3Size, setExtraImage3Size, extraImage3ZIndex, setExtraImage3ZIndex,
    extraImage3Opacity, setExtraImage3Opacity, extraImage3X, setExtraImage3X, extraImage3Y, setExtraImage3Y, defaultArtistPhoto, setDefaultArtistPhoto, artistPhotoSizeMode, setArtistPhotoSizeMode,
    artistPhotoSizePx, setArtistPhotoSizePx, textTitle, setTextTitle, textSubtitle, setTextSubtitle,
    textVotingNow, setTextVotingNow, textWaiting, setTextWaiting, sound12PointsUrl, setSound12PointsUrl,
    soundWinnerUrl, setSoundWinnerUrl,
    confettiEnabled, setConfettiEnabled, confettiColors, setConfettiColors, top1Color, setTop1Color,
    top2Color, setTop2Color, top3Color, setTop3Color, lastPlaceColor, setLastPlaceColor,
    top1Translucent, setTop1Translucent, top2Translucent, setTop2Translucent, top3Translucent, setTop3Translucent,
    lastPlaceTranslucent, setLastPlaceTranslucent, positionBadgeTranslucent, setPositionBadgeTranslucent, votingColumnCenter, setVotingColumnCenter, leftColSpan, setLeftColSpan,
    rightColSpan, setRightColSpan, votingColSpan, setVotingColSpan, columnGap, setColumnGap,
    votingWidthCustom, setVotingWidthCustom, votingWidthSpan, setVotingWidthSpan,
    voteBarColorMode, setVoteBarColorMode, voteBarColor1, setVoteBarColor1, voteBarColor2, setVoteBarColor2,
    voteBarTranslucent, setVoteBarTranslucent, voteBarOpacity, setVoteBarOpacity,
    subtitleCounterEnabled, setSubtitleCounterEnabled, subtitlePhaseOrder, setSubtitlePhaseOrder, subtitleJuryLabel, setSubtitleJuryLabel,
    subtitleTelevoteLabel, setSubtitleTelevoteLabel, mystery12Text, setMystery12Text, mysteryHiddenPoints, setMysteryHiddenPoints, voteDisplayColumns, setVoteDisplayColumns,
    showVoterBorder, setShowVoterBorder, showJuryBadge, setShowJuryBadge, fontVotingSecondary, setFontVotingSecondary,
    fontTitleItalic, setFontTitleItalic, fontSubtitleItalic, setFontSubtitleItalic, fontCountryItalic, setFontCountryItalic,
    fontPointsItalic, setFontPointsItalic, fontVotingItalic, setFontVotingItalic, fontVotingSecondaryItalic, setFontVotingSecondaryItalic,
    customFontVotingSecondaryUrl, setCustomFontVotingSecondaryUrl, customFontVotingSecondaryName, setCustomFontVotingSecondaryName, overlay12Enabled, setOverlay12Enabled,
    overlay12DurationMode, setOverlay12DurationMode, overlay12DurationSeconds, setOverlay12DurationSeconds, overlay12BgStyle, setOverlay12BgStyle,
    overlay12BgColor, setOverlay12BgColor, overlay12BgOpacity, setOverlay12BgOpacity, overlay12TextReceives, setOverlay12TextReceives,
    overlay12UseAdminColors, setOverlay12UseAdminColors, overlay12ArrowColor, setOverlay12ArrowColor, overlay12ArrowThickness, setOverlay12ArrowThickness, overlay12ArrowHeadSize, setOverlay12ArrowHeadSize, overlay12ShowConfetti, setOverlay12ShowConfetti, overlay12Visible, setOverlay12Visible, previewGiverId, setPreviewGiverId,
    previewRecipientId, setPreviewRecipientId, winnerOverlayEnabled, setWinnerOverlayEnabled, winnerOverlayDurationMode, setWinnerOverlayDurationMode,
    winnerOverlayDurationSeconds, setWinnerOverlayDurationSeconds, winnerOverlayBgStyle, setWinnerOverlayBgStyle, winnerOverlayBgColor, setWinnerOverlayBgColor,
    winnerOverlayBgOpacity, setWinnerOverlayBgOpacity, winnerOverlayTitleText, setWinnerOverlayTitleText, winnerOverlaySubtitleText, setWinnerOverlaySubtitleText,
    winnerOverlayUseAdminColors, setWinnerOverlayUseAdminColors, winnerOverlayAccentColor, setWinnerOverlayAccentColor, winnerOverlayIcon, setWinnerOverlayIcon, winnerOverlayIconUrl, setWinnerOverlayIconUrl, winnerOverlayShowConfetti, setWinnerOverlayShowConfetti, winnerOverlayShowPoints, setWinnerOverlayShowPoints,
    winnerOverlayVisible, setWinnerOverlayVisible, overlay12StyleCustom, setOverlay12StyleCustom, overlay12FontTitle, setOverlay12FontTitle,
    overlay12FontCountry, setOverlay12FontCountry, overlay12FontSecondary, setOverlay12FontSecondary, overlay12SizeScale, setOverlay12SizeScale,
    winnerOverlayStyleCustom, setWinnerOverlayStyleCustom, winnerOverlayFontTitle, setWinnerOverlayFontTitle, winnerOverlayFontCountry, setWinnerOverlayFontCountry,
    winnerOverlayFontSecondary, setWinnerOverlayFontSecondary, winnerOverlaySizeScale, setWinnerOverlaySizeScale, loading, setLoading,
    exportingPdf, setExportingPdf, winnerPreviewId, setWinnerPreviewId, adminInitRef, adminPollRef,
    navigate, fetchCurrentVoterStatus, handleHideOverlay12, handlePreviewOverlay12, handleShowWinner, handleHideWinner,
    handleShowTop6, handleHideTop6, top6OverlayVisible, top6TitleText, setTop6TitleText,
    top6StyleCustom, setTop6StyleCustom, top6FontTitle, setTop6FontTitle, top6FontCountry, setTop6FontCountry,
    overlay12CustomFontUrl, setOverlay12CustomFontUrl, winnerCustomFontUrl, setWinnerCustomFontUrl,
    getFontFormat, fetchBackground, handleSetBackground, handleExportPdf, handleFileUpload, fetchCountries,
    fetchVotes, downloadFromApi, handleExportVotesPdf, handleExportVotesExcel, fetchEvents, reloadEventData,
    handleSelectEvent, handleCreateEvent, handleRenameEvent, handleExportEvent, handleImportEvent, handleAddCountry,
    handleDeleteCountry, handleSetCurrentVoter, handleResetVotes, handleLogout, handleApplyVotes, handleUnapplyAllVotes, handleReveal12Points,
    handleEditCountry, handleUpdateCountry, handleToggleBadge, handleToggleTranslucent, handleResetAllBarColors, votingCountries, juryMembers,
    votedCountries,
  };

  return (
    <AdminContext.Provider value={adminCtx}>
    <div className="min-h-screen bg-zinc-950 p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 data-testid="admin-title" className="font-unbounded font-bold text-2xl sm:text-3xl md:text-5xl text-white">Panel Admin</h1>
            <p className="text-zinc-400 mt-1 sm:mt-2 text-sm sm:text-base">Gestión del sistema de votación</p>
            <div className="mt-3 flex items-center gap-2">
              <Select value={activeEvent?.id || ""} onValueChange={handleSelectEvent}>
                <SelectTrigger data-testid="event-selector" className="w-full sm:w-[280px] bg-zinc-900 border-zinc-700 text-white">
                  <SelectValue placeholder="Selecciona un evento" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700 text-white max-h-[320px]">
                  {events.map(ev => (
                    <SelectItem key={ev.id} value={ev.id} data-testid={`event-option-${ev.id}`} className="focus:bg-zinc-800">
                      {ev.name}
                    </SelectItem>
                  ))}
                  <SelectItem value="__create__" data-testid="event-create-option" className="text-pink-400 font-semibold focus:bg-zinc-800">
                    + Crear nuevo evento
                  </SelectItem>
                </SelectContent>
              </Select>
              <Button
                data-testid="event-rename-btn"
                onClick={() => { setRenameEventName(activeEvent?.name || ""); setShowRenameEvent(true); }}
                variant="outline"
                size="icon"
                title="Renombrar evento"
                className="bg-zinc-900 border-zinc-700 hover:bg-zinc-800 shrink-0"
              >
                <Edit2 className="w-4 h-4" />
              </Button>
              {events.length > 1 && (
                <Button
                  data-testid="event-delete-btn"
                  onClick={() => { setDeleteEventConfirmText(""); setShowDeleteEvent(true); }}
                  variant="outline"
                  size="icon"
                  title="Eliminar evento"
                  className="bg-red-900/20 border-red-800 text-red-400 hover:bg-red-900/40 shrink-0"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button 
              data-testid="btn-logout"
              onClick={handleLogout} 
              variant="outline" 
              size="sm"
              className="flex-1 sm:flex-none bg-red-900/20 border-red-800 text-red-400 hover:bg-red-900/40 sm:h-10 sm:px-4"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar Sesión
            </Button>
            <Button 
              data-testid="back-home-btn"
              onClick={() => navigate('/')} 
              variant="outline" 
              size="sm"
              className="flex-1 sm:flex-none bg-zinc-900 border-zinc-700 hover:bg-zinc-800 sm:h-10 sm:px-4"
            >
              <Home className="w-4 h-4 mr-2" />
              Inicio
            </Button>
          </div>
        </div>

        {/* Crear nuevo evento */}
        <Dialog open={showCreateEvent} onOpenChange={setShowCreateEvent}>
          <DialogContent className="bg-zinc-900 border-zinc-700 text-white" data-testid="create-event-dialog">
            <DialogHeader>
              <DialogTitle>Crear nuevo evento</DialogTitle>
              <DialogDescription className="text-zinc-400">
                Se creará una instancia independiente con su propia configuración, países, jurados y votos.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Nombre del evento</Label>
                <Input
                  data-testid="new-event-name-input"
                  value={newEventName}
                  onChange={(e) => setNewEventName(e.target.value)}
                  placeholder="Ej: Eurovisión 2026"
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              <div className="space-y-2">
                <Label>Copiar datos de un evento existente (opcional)</Label>
                <Select value={copyFromEventId} onValueChange={setCopyFromEventId}>
                  <SelectTrigger data-testid="copy-from-select" className="bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700 text-white max-h-[280px]">
                    <SelectItem value="none" className="focus:bg-zinc-800">No copiar (evento vacío)</SelectItem>
                    {events.map(ev => (
                      <SelectItem key={ev.id} value={ev.id} className="focus:bg-zinc-800">{ev.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateEvent(false)} className="bg-zinc-800 border-zinc-700 hover:bg-zinc-700">Cancelar</Button>
              <Button data-testid="confirm-create-event-btn" onClick={handleCreateEvent} disabled={creatingEvent} className="bg-pink-600 hover:bg-pink-500">
                {creatingEvent ? "Creando..." : "Crear evento"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Renombrar evento */}
        <Dialog open={showRenameEvent} onOpenChange={setShowRenameEvent}>
          <DialogContent className="bg-zinc-900 border-zinc-700 text-white" data-testid="rename-event-dialog">
            <DialogHeader>
              <DialogTitle>Renombrar evento</DialogTitle>
              <DialogDescription className="text-zinc-400">
                Cambia el nombre del evento activo. Esto no afecta a sus datos.
              </DialogDescription>
            </DialogHeader>
            <div className="py-2 space-y-2">
              <Label>Nuevo nombre</Label>
              <Input
                data-testid="rename-event-input"
                value={renameEventName}
                onChange={(e) => setRenameEventName(e.target.value)}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRenameEvent(false)} className="bg-zinc-800 border-zinc-700 hover:bg-zinc-700">Cancelar</Button>
              <Button data-testid="confirm-rename-event-btn" onClick={handleRenameEvent} className="bg-pink-600 hover:bg-pink-500">Guardar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Eliminar evento (confirmación reforzada) */}
        <AlertDialog open={showDeleteEvent} onOpenChange={(o) => { setShowDeleteEvent(o); if (!o) setDeleteEventConfirmText(""); }}>
          <AlertDialogContent className="bg-zinc-900 border-zinc-700 text-white" data-testid="delete-event-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-red-400 flex items-center gap-2">
                <Trash2 className="w-5 h-5" /> Eliminar evento
              </AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                Vas a eliminar de forma <strong className="text-red-400">permanente</strong> el evento <strong className="text-white">&quot;{activeEvent?.name}&quot;</strong> junto con TODOS sus países, jurados, configuración y votos. <strong className="text-red-400">Esta acción no se puede deshacer.</strong>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-2 space-y-2">
              <Label className="text-zinc-300">Para confirmar, escribe el nombre exacto del evento:</Label>
              <p className="text-zinc-500 text-sm select-none">{activeEvent?.name}</p>
              <Input
                data-testid="delete-event-confirm-input"
                value={deleteEventConfirmText}
                onChange={(e) => setDeleteEventConfirmText(e.target.value)}
                placeholder="Escribe el nombre del evento"
                autoComplete="off"
                className="bg-zinc-800 border-zinc-700"
              />
              <p className="text-zinc-600 text-xs">Sugerencia: si lo creaste sin querer, también puedes exportarlo primero como copia de seguridad.</p>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setDeleteEventConfirmText("")} className="bg-zinc-800 border-zinc-700 hover:bg-zinc-700 text-white">Cancelar</AlertDialogCancel>
              <AlertDialogAction
                data-testid="confirm-delete-event-btn"
                disabled={deletingEvent || deleteEventConfirmText.trim() !== (activeEvent?.name || "")}
                onClick={(e) => { e.preventDefault(); handleDeleteEvent(); }}
                className="bg-red-600 hover:bg-red-500 text-white disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {deletingEvent ? "Eliminando..." : "Eliminar definitivamente"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Confirmar importación */}
        <AlertDialog open={showImportConfirm} onOpenChange={setShowImportConfirm}>
          <AlertDialogContent className="bg-zinc-900 border-zinc-700 text-white" data-testid="import-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>¿Importar datos del evento?</AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                Esta acción <strong className="text-red-400">borrará todos los datos actuales</strong> del evento &quot;{activeEvent?.name}&quot; (países, jurados, configuración y votos) y los reemplazará por los del archivo seleccionado. Esta acción no se puede deshacer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setImportFile(null)} className="bg-zinc-800 border-zinc-700 hover:bg-zinc-700 text-white">Cancelar</AlertDialogCancel>
              <AlertDialogAction data-testid="confirm-import-btn" onClick={handleImportEvent} disabled={importing} className="bg-red-600 hover:bg-red-500">
                {importing ? "Importando..." : "Sí, importar y reemplazar"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Tabs defaultValue="countries" className="w-full">
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 h-auto gap-1 bg-zinc-900 border border-zinc-800 p-1">
            <TabsTrigger data-testid="tab-countries" value="countries" className="whitespace-normal text-center text-xs sm:text-sm py-2 h-auto data-[state=active]:bg-pink-600">Países y Jurado</TabsTrigger>
            <TabsTrigger data-testid="tab-votes" value="votes" className="whitespace-normal text-center text-xs sm:text-sm py-2 h-auto data-[state=active]:bg-pink-600">Votos Recibidos</TabsTrigger>
            <TabsTrigger data-testid="tab-control" value="control" className="whitespace-normal text-center text-xs sm:text-sm py-2 h-auto data-[state=active]:bg-pink-600">Control Pantalla</TabsTrigger>
            <TabsTrigger data-testid="tab-config" value="config" className="whitespace-normal text-center text-xs sm:text-sm py-2 h-auto data-[state=active]:bg-pink-600">Configuración</TabsTrigger>
          </TabsList>

          <CountriesTab />

          <VotesTab />

          <ControlTab />

          <ConfigTab />
        </Tabs>
      </div>
    </div>
    </AdminContext.Provider>
  );
};

export default AdminDashboard;